import { Check, ChevronLeft, ChevronRight, Copy, LoaderCircle, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { Diagnostic } from '../api/types';
import { usePermission } from '../auth/context';
import { requiredRole, type Permission } from '../auth/roles';
import { strings } from '../strings';
import { trapTab } from '../lib/focusTrap';
import { severityPresentation, type Presentation } from './presentation';
import { describeError } from './toast';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  icon?: ReactNode;
};

function variantClass(variant: ButtonProps['variant']) {
  switch (variant) {
    case 'primary':
      return 'btn btn-primary';
    case 'danger':
      return 'btn btn-danger';
    case 'ghost':
      return 'btn btn-ghost';
    default:
      return 'btn';
  }
}

export function Button({ variant, icon, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={`${variantClass(variant)} ${className ?? ''}`} {...rest}>
      {icon}
      {children}
    </button>
  );
}

/**
 * A button for an action that needs a role. Without the role it stays focusable but is
 * aria-disabled, with a tooltip naming the role (spec 6.1.4). `disabledReason` disables it for other reasons.
 */
export function ActionButton({
  permission,
  disabledReason,
  onClick,
  children,
  title,
  ...rest
}: ButtonProps & { permission: Permission; disabledReason?: string | null }) {
  const allowed = usePermission(permission);
  const reasonId = useId();
  const reason = !allowed ? strings.common.requiresRole(requiredRole(permission)) : (disabledReason ?? null);
  const disabled = reason !== null || rest.disabled;
  return (
    <>
      <Button
        {...rest}
        disabled={undefined}
        aria-disabled={disabled ? true : undefined}
        aria-describedby={reason ? reasonId : undefined}
        title={reason ?? title}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onClick?.(event);
        }}
      >
        {children}
      </Button>
      {reason && (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
    </>
  );
}

export function IconButton({
  label,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`btn btn-ghost btn-icon ${className ?? ''}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function StatusBadge({ presentation, className }: { presentation: Presentation; className?: string }) {
  const Icon = presentation.icon;
  return (
    <span className={`badge tone-${presentation.tone} ${className ?? ''}`}>
      <Icon aria-hidden className={`size-3.5 ${presentation.spin ? 'animate-spin' : ''}`} />
      {presentation.label}
    </span>
  );
}

export function Loading({ label = strings.common.loading }: { label?: string }) {
  return (
    <p role="status" className="flex items-center gap-2 p-4 text-sm text-slate-600 dark:text-slate-300">
      <LoaderCircle aria-hidden className="size-4 animate-spin" />
      {label}
    </p>
  );
}

export function ErrorMessage({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { title, detail } = describeError(error);
  return (
    <div role="alert" className="tone-danger m-4 rounded-lg border p-3 text-sm">
      <p className="font-semibold">{title}</p>
      {detail && <p>{detail}</p>}
      {onRetry && (
        <Button className="mt-2" onClick={onRetry}>
          {strings.common.retry}
        </Button>
      )}
    </div>
  );
}

export function PageHeader({
  title,
  actions,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold">{title}</h1>
        {children}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Pager({
  hasPrevious,
  nextCursor,
  onPrevious,
  onNext,
}: {
  hasPrevious: boolean;
  nextCursor: string | null | undefined;
  onPrevious: () => void;
  onNext: (cursor: string) => void;
}) {
  if (!hasPrevious && !nextCursor) return null;
  return (
    <nav aria-label="Pagination" className="mt-3 flex gap-2">
      <Button
        disabled={!hasPrevious}
        onClick={onPrevious}
        icon={<ChevronLeft aria-hidden className="size-4" />}
      >
        {strings.common.previous}
      </Button>
      <Button disabled={!nextCursor} onClick={() => nextCursor && onNext(nextCursor)}>
        {strings.common.next}
        <ChevronRight aria-hidden className="size-4" />
      </Button>
    </nav>
  );
}

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      label={copied ? strings.common.copied : label}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard access can be denied; nothing else to do.
        }
      }}
    >
      {copied ? <Check aria-hidden className="size-4" /> : <Copy aria-hidden className="size-4" />}
    </IconButton>
  );
}

/**
 * Modal dialog: focus moves into it on open, stays inside while open, and goes back on close; Escape
 * closes it.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
  className,
  describedBy,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Extra classes of the panel (for example `dialog-sheet`: a full-screen sheet on phones). */
  className?: string;
  /** The ID of the element that describes the dialog (`aria-describedby`). */
  describedBy?: string;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-autofocus]',
    );
    (first ?? panel.current)?.focus();
    return () => previous?.focus?.();
  }, []);

  // A body that scrolls is a tab stop, so keyboard users can scroll it (WCAG 2.1.1, axe scrollable-region-focusable).
  const body = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const element = body.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setScrollable(element.scrollHeight > element.clientHeight + 1));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--scrim)] p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onCloseRef.current();
          } else if (panel.current) {
            trapTab(event, panel.current);
          }
        }}
        className={`flex max-h-[90vh] w-full flex-col rounded-[10px] border border-line bg-surface shadow-float ${wide ? 'max-w-5xl' : 'max-w-xl'} ${className ?? ''}`}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <IconButton label={strings.common.close} onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        </div>
        <div ref={body} className="overflow-y-auto px-4 py-3" tabIndex={scrollable ? 0 : undefined}>
          {children}
        </div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function DiagnosticItem({ diagnostic }: { diagnostic: Diagnostic }) {
  const presentation = severityPresentation[diagnostic.severity];
  const Icon = presentation.icon;
  return (
    <span className="flex items-start gap-2 text-sm">
      <Icon
        aria-hidden
        className={`mt-0.5 size-4 shrink-0 ${diagnostic.severity === 'error' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}
      />
      <span className="sr-only">{presentation.label}:</span>
      <code className="font-mono text-xs font-semibold">{diagnostic.code}</code>
      <span className="min-w-0 break-words">{diagnostic.message}</span>
    </span>
  );
}

export function DiagnosticList({ diagnostics }: { diagnostics: readonly Diagnostic[] }) {
  if (diagnostics.length === 0) return null;
  return (
    <ul className="space-y-1">
      {diagnostics.map((diagnostic, i) => (
        <li key={i}>
          <DiagnosticItem diagnostic={diagnostic} />
        </li>
      ))}
    </ul>
  );
}

/** Errors shown next to a form field. */
export function FieldMessages({ id, messages }: { id?: string; messages: readonly string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul id={id} className="field-error">
      {messages.map((message, i) => (
        <li key={i}>{message}</li>
      ))}
    </ul>
  );
}

export function Field({
  label,
  htmlFor,
  help,
  errors = [],
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  help?: ReactNode;
  errors?: readonly string[];
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <label htmlFor={htmlFor} className="label">
        {label}
        {required && (
          <>
            <span aria-hidden className="text-red-700 dark:text-red-300">
              {' '}
              *
            </span>
            <span className="sr-only"> ({strings.common.required})</span>
          </>
        )}
      </label>
      {children}
      {help && (
        <p id={`${htmlFor}-help`} className="help">
          {help}
        </p>
      )}
      <FieldMessages id={`${htmlFor}-error`} messages={errors} />
    </div>
  );
}
